import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonServices } from '@asamco/base';
import { ApplicationType, ParameterType as ASParameterType } from '@asamco/management-portal/shared';
import { FormHelpers } from '@asamco/utils';
import { L10nLocale, L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import swal from 'sweetalert2';
import { ParameterType as ALParameterType } from '../../../enums/parameter-type.enum';
import { ApplicationParams } from '../../../interfaces/application-login-params.interface';
import { ApplicationTypeParameterLogin } from '../../../interfaces/application-type-param-login.interface';
import { Application } from '../../../interfaces/application.interface';
import { AccessLevel } from '../../../interfaces/setting-template.interface';
import { ApplicationLoginParamsService } from '../../../services/application-login-params.service';
import { ApplicationLoginService } from '../../../services/application-login.service';
import { ApplicationService } from '../../../services/application.service';
import { AsValidatorDirective } from '../../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTooltipModule, DxSelectBoxModule, DxCheckBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
    selector: 'al-link-edit-auth-generic-modal',
    templateUrl: './link-edit-auth-generic-modal.component.html',
    styleUrls: ['./link-edit-auth-generic-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        DxTooltipModule,
        DxSelectBoxModule,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        DxCheckBoxModule,
        AsValidatorDirective,
        DxTextBoxModule,
        AsyncPipe,
        L10nTranslatePipe,
    ],
})
export class LinkEditAuthGenericModalComponent implements OnInit, OnDestroy {
	/** Output for saving. */
	@Output()
	saved: EventEmitter<ApplicationType> = new EventEmitter<ApplicationType>();

	/** Boolean for showing/hiding modal */
	show: boolean = false;

	/** Keeping the current id for parameter identification */
	currentId: number = 0;

	/** The ID of the application */
	currentApplicationId: number;

	/** Types of parameters that can be added */
	public readonly parameterTypes: Array<{
		text: string;
		id: ASParameterType | ALParameterType;
	}> = [
		{ id: ASParameterType.ConnectionString, text: 'Connection string' },
		{ id: ASParameterType.String, text: 'String' },
		{ id: ASParameterType.Integer, text: 'Integer' },
		{ id: ASParameterType.Password, text: 'Password' },
		{ id: ASParameterType.Email, text: 'Email' },
		{ id: ALParameterType.Dropdown, text: 'Dropdown' },
	];

	/** Form for validation */
	applicationTypeForm: FormGroup;

	/** Boolean to check if OAuth is being used */
	isOAuthEnabled: boolean = false;

	/** Current application type object */
	applications$: Observable<Application[]>;
	applicationLoginFields: string[];

	private destroy$ = new Subject<void>();

	/** Modal title */
	title: string;

	applicationParams: ApplicationParams;
	/** Subject used for tracking changes, so that the UI gets updated using async */
	applicationParamsChange$ = new Subject<any>();

	passwordButton: any;
	passwordMode: any;

	constructor(
		private formBuilder: FormBuilder,
		private cs: CommonServices,
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		private applicationService: ApplicationService,
		private appLoginParamsService: ApplicationLoginParamsService,
		private applicationLoginService: ApplicationLoginService,
		private cdRef: ChangeDetectorRef
	) {}

	public get accessLevel(): typeof AccessLevel {
		return AccessLevel;
	}

	loadApplicationSettings(applicationId: number) {
		this.loadApplicationParams(applicationId);
	}

	ngOnInit() {
		this.retrieveApplicationLoginFields();
		this.applications$ = this.applicationService.get().pipe(takeUntil(this.destroy$));
		this.applicationParamsChange$.pipe(takeUntil(this.destroy$)).subscribe(e => {
			if (e.toDelete) {
				this.applicationParams.parameters = this.applicationParams.parameters.filter(item => item.id !== e.item);
			} else {
				this.applicationParams = e.item;
				this.isOAuthEnabled = e.item.oAuth;
			}
		});
	}

	/** Show the modal with the passed application type. */
	showModal() {
		this.show = true;
		this.title = 'Manage application authentication layout';
		this.cdRef.detectChanges();
	}

	retrieveApplicationLoginFields() {
		//Don't make it an observable, otherwise it'll be executed each time an row is visible, and we only need to retrieve it once
		this.applicationLoginService
			.getFields()
			.pipe(takeUntil(this.destroy$))
			.subscribe(e => {
				this.applicationLoginFields = e;
				this.applicationLoginFields.sort();
			});
	}

	clearFormAndParams() {
		this.applicationTypeForm = this.formBuilder.group({});
		this.applicationParams = {
			parameters: new Array<ApplicationTypeParameterLogin>(),
			applicationType: '',
		} as ApplicationParams;
	}

	loadApplicationParams(applicationForm: any) {
		this.currentApplicationId = applicationForm.value;
		//We create a new formbuilder group upon refresh, because otherwise we have to delete everything manually
		//this.applicationTypeForm = this.formBuilder.group({});
		this.clearFormAndParams();

		this.appLoginParamsService
			.getParams(applicationForm.value)
			.pipe(takeUntil(this.destroy$))
			.subscribe(e => {
				if (e !== null) {
					//We load the entry and check for changes
					this.applicationParamsChange$.next({ item: e, toDelete: false });
					//We load in the controls that we retrieved
					this.loadControls();
				} else {
					//We create a new entry
					const newParam = {
						parameters: new Array<ApplicationTypeParameterLogin>(),
						applicationType: '',
					} as ApplicationParams;
					this.applicationParamsChange$.next({ item: newParam });
					this.addParameter();
				}
			});
	}

	loadControls() {
		for (const param of this.applicationParams.parameters) {
			this.currentId++;
			param.id = this.currentId;
			this.applicationTypeForm.addControl(this.currentId + '_type', new FormControl(param.type, Validators.required));
			this.applicationTypeForm.addControl(this.currentId + '_name', new FormControl(param.name, Validators.required));
			this.applicationTypeForm.addControl(this.currentId + '_defaultValue', new FormControl(param.defaultValue));
			this.applicationTypeForm.addControl(this.currentId + '_fieldName', new FormControl(param.fieldName, Validators.required));
			this.applicationTypeForm.addControl(this.currentId + '_richText', new FormControl(param.richText));
		}
	}

	/** Making sure the user doesn't input a string into a numberbox as a default value */
	/** Alternative for Validator because switching that can cause hiccups/odd behaviour */
	checkDefaultValueNumberBox(form: FormGroup): boolean {
		// tslint:disable-next-line:forin
		for (const field in form.controls) {
			//Split based up the underscore, so we get the ID and the control type
			const splitted = field.split('_', 2);
			//Checking for default value
			if (splitted[1] === 'defaultValue') {
				//Get the involved controls
				const controlDefaultValue = form.get(splitted[0] + '_defaultValue');
				const controlType = form.get(splitted[0] + '_type');
				//Check if the selected type is an integer, then we check for validity
				if (controlType.value === ASParameterType.Integer) {
					//If it's empty, ignore
					if (!controlDefaultValue.value) {
						controlDefaultValue.setErrors(null);
						continue;
					}
					//Check if it's possible to parse, otherwise throw an exception
					if (!Number(controlDefaultValue.value)) {
						controlDefaultValue.setErrors({ 'incorrect default value type': true });
						return false;
					} else {
						//Otherwise there are no exceptions
						controlDefaultValue.setErrors(null);
					}
				}
			}
		}
		return true;
	}

	/** Save the application type. */
	save() {
		if (FormHelpers.validateForm(this.applicationTypeForm) && this.checkDefaultValueNumberBox(this.applicationTypeForm)) {
			this.applicationParams.applicationId = this.currentApplicationId;
			this.applicationParams.oAuth = this.isOAuthEnabled;

			// Delete the id which is used for internal
			// identification.
			for (const param of this.applicationParams.parameters) {
				delete param.id;
			}
			this.appLoginParamsService.saveParams(this.applicationParams).subscribe(
				_ => {
					swal.fire({
						icon: 'success',
						text: 'Successfully saved entries',
					});
				},
				err => {
					swal.fire({
						icon: 'warning',
						text: 'Error saving applicationlogin',
					});
				}
			);
		} else {
			swal.fire({
				icon: 'warning',
				text: this.cs.ts.translate('APPLICATIONTYPES.DIALOG.VALIDATION_INCORRECT'),
			});
		}
	}

	/** Hide the modal. */
	cancel() {
		this.show = false;
	}

	/**
	 * Adds a parameter to the list of parameters
	 * and adds the controls to the form for validatrion.
	 */
	addParameter() {
		this.currentId++;
		this.applicationParams.parameters.push({
			id: this.currentId,
			type: ASParameterType.String,
			isRequired: false,
			hidden: false,
			fieldName: null,
			richText: '',
			defaultValue: null,
		} as ApplicationTypeParameterLogin);
		const newParam = this.applicationParams.parameters[this.applicationParams.parameters.length - 1];
		this.applicationTypeForm.addControl(this.currentId + '_type', new FormControl(newParam.type, Validators.required));
		this.applicationTypeForm.addControl(this.currentId + '_name', new FormControl(newParam.name, Validators.required));
		this.applicationTypeForm.addControl(this.currentId + '_defaultValue', new FormControl(newParam.defaultValue));
		this.applicationTypeForm.addControl(this.currentId + '_fieldName', new FormControl(newParam.fieldName, Validators.required));
		this.applicationTypeForm.addControl(this.currentId + '_richText', new FormControl(newParam.richText));
	}

	/**
	 * Removes a parameter to the list of parameters
	 * and removes the controls to the form for validatrion.
	 */
	removeParameter(id: number) {
		swal.fire({
			showCancelButton: true,
			title: this.cs.ts.translate('SWAL_TITLE.WARNING'),
			text: 'Are you sure you want to remove this parameter?',
			icon: 'warning',
		}).then(r => {
			if (r.value) {
				this.applicationParamsChange$.next({ item: id, toDelete: true });
				//this.applicationParams.parameters = this.applicationParams.parameters.filter(item => item.id !== id);
				this.applicationTypeForm.removeControl(id + '_type');
				this.applicationTypeForm.removeControl(id + '_name');
				this.applicationTypeForm.removeControl(id + '_defaultValue');
				this.applicationTypeForm.removeControl(id + '_fieldName');
				this.applicationTypeForm.removeControl(id + '_richText');
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
